import {
  IMPACT_CAMPAIGN_HIDE_UNREDEEMED_STORAGE_KEY_PREFIX,
  IMPACT_CAMPAIGN_RECENTS_COLLAPSED_STORAGE_KEY_PREFIX,
  IMPACT_CAMPAIGN_RECENTS_MAX,
  IMPACT_CAMPAIGN_RECENTS_STORAGE_KEY_PREFIX,
} from "@/constants/impactReport";

/*
 * Storage is injected rather than read off the global so these stay pure and safe outside the
 * browser; an absent store behaves as an empty one.
 */

function recentsKey(userId: string): string {
  return `${IMPACT_CAMPAIGN_RECENTS_STORAGE_KEY_PREFIX}${userId}`;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

/** The user's recently applied campaign ids, newest first; [] when absent or unreadable. */
export function readRecentImpactCampaignIds(
  storage: Storage | undefined,
  userId: string,
): string[] {
  if (!storage) return [];
  const key = recentsKey(userId);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return [];
  }
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed)) return parsed;
  } catch {
    // Falls through to the warning below.
  }
  console.warn(`Ignoring malformed recent campaigns in storage key "${key}"`);
  return [];
}

/**
 * Puts `appliedIds` (in order) ahead of the previous recents, de-duplicated and capped at
 * `IMPACT_CAMPAIGN_RECENTS_MAX`, writes it back, and returns it. A write failure (quota, private
 * mode) is only warned about — the returned list is still correct for this session.
 */
export function rememberRecentImpactCampaignIds(
  storage: Storage | undefined,
  userId: string,
  appliedIds: string[],
): string[] {
  const next = [...new Set([...appliedIds, ...readRecentImpactCampaignIds(storage, userId)])].slice(
    0,
    IMPACT_CAMPAIGN_RECENTS_MAX,
  );
  if (!storage) return next;
  const key = recentsKey(userId);
  try {
    storage.setItem(key, JSON.stringify(next));
  } catch (error) {
    console.warn(`Could not save recent campaigns to storage key "${key}"`, error);
  }
  return next;
}

function recentsCollapsedKey(userId: string): string {
  return `${IMPACT_CAMPAIGN_RECENTS_COLLAPSED_STORAGE_KEY_PREFIX}${userId}`;
}

/** Whether the user folded the picker's Recent section away; false when absent or unreadable. */
export function readImpactCampaignRecentsCollapsed(
  storage: Storage | undefined,
  userId: string,
): boolean {
  if (!storage) return false;
  const key = recentsCollapsedKey(userId);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return false;
  }
  if (raw === null) return false;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "boolean") return parsed;
  } catch {
    // Falls through to the warning below.
  }
  console.warn(`Ignoring malformed Recent collapsed state in storage key "${key}"`);
  return false;
}

/** Writes the Recent section's collapsed state; a write failure is only warned about. */
export function rememberImpactCampaignRecentsCollapsed(
  storage: Storage | undefined,
  userId: string,
  collapsed: boolean,
): void {
  if (!storage) return;
  const key = recentsCollapsedKey(userId);
  try {
    storage.setItem(key, JSON.stringify(collapsed));
  } catch (error) {
    console.warn(`Could not save the Recent collapsed state to storage key "${key}"`, error);
  }
}

function hideUnredeemedKey(userId: string): string {
  return `${IMPACT_CAMPAIGN_HIDE_UNREDEEMED_STORAGE_KEY_PREFIX}${userId}`;
}

/** Whether the picker hides campaigns with no redemptions; true when absent or unreadable. */
export function readImpactCampaignHideUnredeemed(
  storage: Storage | undefined,
  userId: string,
): boolean {
  if (!storage) return true;
  const key = hideUnredeemedKey(userId);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return true;
  }
  if (raw === null) return true;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "boolean") return parsed;
  } catch {
    // Falls through to the warning below.
  }
  console.warn(`Ignoring malformed hide-unredeemed preference in storage key "${key}"`);
  return true;
}

/** Writes the picker's hide-unredeemed preference; a write failure is only warned about. */
export function rememberImpactCampaignHideUnredeemed(
  storage: Storage | undefined,
  userId: string,
  hide: boolean,
): void {
  if (!storage) return;
  const key = hideUnredeemedKey(userId);
  try {
    storage.setItem(key, JSON.stringify(hide));
  } catch (error) {
    console.warn(`Could not save the hide-unredeemed preference to storage key "${key}"`, error);
  }
}
